import Header from '../Components/Header';
import Footer from '../Components/Footer';
import DeliverableSection from '../Components/DeliverableSection';
import { Helmet } from 'react-helmet';
import { SITE_ORIGIN, canonicalUrl } from '../siteConfig';
import { SERVICE_AREAS, SCHEMA_AREA_SERVED } from '../serviceAreas';

const LINEUP_UPDATED = 'July 2026';
const PAGE_URL = canonicalUrl('/services/storage-and-backup');

const SCHEMA_SUB_SERVICES = [
  {
    name: 'QNAP NAS Deployment',
    description: 'Rack mount NAS installation with RAID, shared folders, and user access.',
  },
  {
    name: 'Microsoft 365 SharePoint and OneDrive Setup',
    description: 'SharePoint team sites, document libraries, and OneDrive sync for cloud-based file storage.',
  },
  {
    name: 'Backup Configuration and Testing',
    description: 'Hybrid Backup Sync and sample restores for on-premise NAS systems. Recovery documentation for Microsoft 365 SharePoint and OneDrive.',
  },
];

function buildStructuredData(faqs) {
  const townAreas = SERVICE_AREAS.map((name) => ({ '@type': 'City', name }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_ORIGIN}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Services',
            item: `${SITE_ORIGIN}/services`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Storage and Backup',
            item: PAGE_URL,
          },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${PAGE_URL}#storage-and-backup`,
        name: 'Storage and Backup',
        description:
          'On-premise QNAP NAS and Microsoft 365 SharePoint and OneDrive setup for businesses in Westchester and Fairfield County, including RAID storage, off-site NAS backups, and cloud file organization.',
        url: PAGE_URL,
        dateModified: '2026-07-04',
        provider: {
          '@type': 'LocalBusiness',
          '@id': `${SITE_ORIGIN}/#localbusiness`,
          name: 'Solace Integrations',
          url: SITE_ORIGIN,
          telephone: '+19145641865',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Hawthorne',
            addressRegion: 'NY',
            addressCountry: 'US',
          },
          areaServed: SCHEMA_AREA_SERVED,
        },
        areaServed: [
          { '@type': 'AdministrativeArea', name: 'Westchester County, NY' },
          { '@type': 'AdministrativeArea', name: 'Fairfield County, CT' },
          ...townAreas,
        ],
        hasPart: SCHEMA_SUB_SERVICES.map((service) => ({
          '@type': 'Service',
          name: service.name,
          description: service.description,
          provider: { '@id': `${SITE_ORIGIN}/#localbusiness` },
          areaServed: townAreas,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${PAGE_URL}#faq`,
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };
}

const DELIVERABLES = [
  {
    title: 'Storage Assessment',
    icon: 'fas fa-hdd',
    image: 'assets/images/storage-assessment.jpg',
    imageAlt: 'Placeholder illustration of storage capacity planning',
    lead: 'Start with where your files live today.',
    body: 'We review how much data you have, who needs access, and whether on-premise storage, Microsoft 365, or a mix of both makes sense. For NAS projects we size drives and RAID. For cloud projects we map SharePoint sites and OneDrive needs.',
    points: [
      'Current file locations and total storage in use',
      'On-premise NAS sizing with RAID recommendations',
      'SharePoint site and library structure for cloud clients',
      'OneDrive rollout plan for staff who need personal sync space',
    ],
  },
  {
    title: 'Backup Plan',
    icon: 'fas fa-shield-alt',
    image: 'assets/images/3-2-1.png',
    imageAlt: 'Placeholder illustration of a backup plan with local and cloud copies',
    lead: 'Copies of important files, on a schedule.',
    body: 'On-premise clients get Hybrid Backup Sync copying NAS folders to off-site cloud storage. For Microsoft 365, we document how your team can use built-in version history and recycle bins to recover deleted files.',
    points: [
      'Which folders or libraries are protected and how often',
      'NAS backup targets (Backblaze, Wasabi, S3, and others)',
      'How staff recover files using SharePoint and OneDrive version history and recycle bins',
      'Who to contact and how to request a restore',
    ],
    reverse: true,
  },
  {
    title: 'Folders & Permissions',
    icon: 'fas fa-folder-open',
    image: 'assets/images/folders.jpg',
    imageAlt: 'Abstract diagram representing shared folder structure',
    lead: 'The right people see the right files.',
    body: 'NAS projects get shared folders with Windows and Mac access mapped by team. Microsoft 365 projects get SharePoint sites for shared work and OneDrive for individual files, with permissions set before anyone starts uploading.',
    points: [
      'Folder or library layout by team or department',
      'User and group permissions for read, write, or admin access',
      'How staff connect from their computers or browsers',
      'Migration plan from an old server, NAS, or scattered local files',
    ],
  },
];

const PROFESSIONAL_DEPLOYMENT = {
  title: 'On-Premise NAS Deployment',
  icon: 'fas fa-tools',
  image: 'assets/images/qnap.png',
  imageAlt: 'Placeholder illustration of a rack-mounted NAS being deployed',
  lead: 'Installed in your rack, ready for your team.',
  body: 'We mount the QNAP in your rack, connect it to the network, set up RAID, create shared folders, configure backup jobs, and run a test restore before your production files move over.',
  points: [
    'Rack install, power, and network connection',
    'RAID configuration and shared folder setup',
    'Hybrid Backup Sync jobs to your cloud provider',
    'Test restore to confirm backups are working',
    'Help moving files from your old server or NAS',
  ],
  reverse: true,
};

const CLOUD_DEPLOYMENT = {
  title: 'Microsoft 365 Cloud Storage',
  icon: 'fas fa-cloud',
  image: 'assets/images/sharepoint.jpg',
  imageAlt: 'Placeholder illustration of cloud storage and backup planning',
  lead: 'SharePoint and OneDrive set up for your team.',
  body: 'For offices that store files in the cloud, we configure Microsoft 365 SharePoint team sites and OneDrive. That includes site structure, library permissions, sync guidance for staff, and notes on how to recover deleted files using Microsoft 365 built-in tools.',
  points: [
    'SharePoint team sites and document libraries by department',
    'OneDrive provisioning and sync setup for staff',
    'Permission groups so access stays scoped to the right people',
    'How staff recover deleted files using version history and recycle bins',
    'Migration help moving files from a NAS, server, or local drives into M365',
  ],
};

const KEY_BENEFITS = [
  {
    title: 'On-premise or cloud',
    text: 'Some offices want a NAS in the rack. Others want files in Microsoft 365. We set up either path and help you decide which fits how your team works.',
  },
  {
    title: 'Backups either way',
    text: 'NAS clients get automated off-site copies through Hybrid Backup Sync. Microsoft 365 clients use built-in version history and recycle bins. We document how your team uses those tools when something needs to be restored.',
  },
  {
    title: 'Tested before handoff',
    text: 'For NAS backups, we restore a sample file or folder. For Microsoft 365, we walk through a file recovery so your team knows the steps. Neither path is foolproof, but you will know what to try before you need it.',
  },
];

const ON_PREMISE_PRODUCTS = [
  {
    name: 'Rack Mount NAS',
    summary:
      'Our most common install: a QNAP mounted in your rack, sized for small office file storage and backup.',
    features: [
      'Fits standard network racks alongside your switch and patch panel',
      'Multiple drive bays with RAID for drive failure protection',
      'Shared folders accessible from Windows and Mac on your network',
    ],
    vendorHref: 'https://www.qnap.com/en/product/series/rackmount',
    vendorLabel: 'View rack NAS on QNAP.com',
  },
  {
    name: 'RAID Storage',
    summary:
      'Multiple drives working together so your files survive a single drive failure. We pick the right RAID level for your NAS and document the layout.',
    features: [
      'Typically RAID 5: one drive can fail without data loss',
      'Hot-swap bays so a failed drive can be replaced without shutting down',
      'Capacity and redundancy balanced to your storage needs',
    ],
    vendorHref: 'https://www.qnap.com/en/solution/raid-management',
    vendorLabel: 'Learn about RAID on QNAP.com',
  },
  {
    name: 'Hybrid Backup Sync',
    summary:
      'QNAP built-in backup tool that copies folders from your NAS to cloud storage automatically.',
    features: [
      'Scheduled backups to Backblaze B2, Wasabi, Amazon S3, and other providers',
      'Restore a single file or a whole folder without special software',
      'Job status alerts so you know if a backup missed a run',
    ],
    vendorHref: 'https://www.qnap.com/en/software/hbs3',
    vendorLabel: 'Learn about Hybrid Backup Sync',
  },
];

const CLOUD_PRODUCTS = [
  {
    name: 'Microsoft 365 SharePoint',
    summary:
      'Shared team storage in the cloud. We set up sites and document libraries so departments have a central place for files without a NAS in the office.',
    features: [
      'Team sites and document libraries organized by department or project',
      'Permission groups so only the right people can edit or view',
      'Built-in version history for file rollbacks, within Microsoft retention limits',
    ],
    vendorHref: 'https://www.microsoft.com/en-us/microsoft-365/sharepoint/collaboration',
    vendorLabel: 'Learn about SharePoint',
  },
  {
    name: 'Microsoft 365 OneDrive',
    summary:
      'Personal file sync and storage for each staff member, tied to their Microsoft 365 account.',
    features: [
      'Desktop sync so files are available offline and backed up to the cloud',
      'Known Folder Move to redirect Desktop and Documents into OneDrive',
      'Deleted file recovery through version history or the recycle bin, within plan limits',
    ],
    vendorHref: 'https://www.microsoft.com/en-us/microsoft-365/onedrive/online-cloud-storage',
    vendorLabel: 'Learn about OneDrive',
  },
];

const WHY_QNAP = [
  'Straightforward rack mount units that fit how most small offices actually store files.',
  'Built-in backup software so you are not paying for a separate backup product on top of the hardware.',
  'Works on your local network like a traditional file server your staff already understand.',
  'Cloud backup through Hybrid Backup Sync without moving day-to-day work off the NAS.',
  'A platform we can support remotely when backup jobs fail or you need a file restored.',
];

const WHY_M365 = [
  'No hardware to rack, power, or maintain in your office.',
  'Staff already sign in with their Microsoft 365 accounts for email.',
  'SharePoint handles shared team files; OneDrive handles personal work.',
  'Built-in version history and recycle bins for everyday file recovery, within Microsoft limits.',
  'A good fit when your team is distributed or you do not want on-premise storage.',
];

const FAQS = [
  {
    question: 'Do you support Google Workspace or Dropbox?',
    answer:
      'For cloud file storage we support Microsoft 365 SharePoint and OneDrive only. If you need on-premise storage, we install QNAP NAS systems with RAID and off-site backup.',
  },
  {
    question: 'Should we use a NAS or Microsoft 365 for files?',
    answer:
      'It depends on how your team works. A NAS keeps files local in your office with RAID protection. SharePoint and OneDrive keep files in the cloud and work well for remote staff. We help you pick the right approach or use both for different needs.',
  },
  {
    question: 'Is a NAS a good fit for a small office?',
    answer:
      'For most offices with a handful to a few dozen staff, yes. A QNAP NAS in your rack gives everyone a shared place for files, with RAID protection and cloud backup on top.',
  },
  {
    question: 'Can it back up to the cloud?',
    answer:
      'Yes. We configure Hybrid Backup Sync to copy your important folders to a cloud provider like Backblaze or Wasabi on a schedule. Your daily work stays on the NAS; the cloud copy is there if you need it.',
  },
  {
    question: 'How is this different from a USB hard drive?',
    answer:
      'A NAS is always on, connected to your network, and usable by your whole team at once. It runs scheduled backups automatically and protects against a failed drive with RAID. A USB drive is fine for one person but does not scale for an office.',
  },
  {
    question: 'Do you test backups after setup?',
    answer:
      'For NAS backups, yes. We restore a sample file or folder so you can see the backup jobs work. For Microsoft 365, we walk your team through recovering a file using version history or the recycle bin. That shows the process, but recovery still depends on your Microsoft 365 plan and how long ago the file was deleted.',
  },
  {
    question: 'Can you move our files from an old server or NAS?',
    answer:
      'Yes. We plan the migration from Windows file servers, older QNAP or Synology units, or scattered local drives. Folder names and permissions are mapped before anything moves.',
  },
  {
    question: 'Can you set up SharePoint and OneDrive for our team?',
    answer:
      'Yes. We create SharePoint sites and libraries, set permissions, configure OneDrive sync, and help move files from your current server or NAS into Microsoft 365.',
  },
  {
    question: 'What happens if a drive fails on a NAS?',
    answer:
      'With RAID configured, the NAS keeps running on the remaining drives. You swap out the failed drive and the NAS rebuilds. We document this in your handoff materials so you or your IT contact know what to do.',
  },
];

function FaqItem({ question, answer }) {
  return (
    <article className="unifi-page-faq-item">
      <h3 className="unifi-page-faq-question">{question}</h3>
      <p className="unifi-page-faq-answer">{answer}</p>
    </article>
  );
}

function ProductCard({ name, summary, features, vendorHref, vendorLabel }) {
  return (
    <article className="unifi-page-product-card">
      <h3 className="unifi-page-product-name">{name}</h3>
      <p className="unifi-page-product-summary">{summary}</p>
      <ul className="unifi-page-product-features">
        {features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      <a
        href={vendorHref}
        target="_blank"
        rel="noopener noreferrer"
        className="unifi-page-product-link"
      >
        {vendorLabel}
      </a>
    </article>
  );
}

export default function StorageAndBackup() {
  const structuredData = buildStructuredData(FAQS);

  return (
    <div>
      <Helmet>
        <title>Storage and Backup in Westchester &amp; Fairfield County | Solace Integrations</title>
        <link rel="canonical" href={PAGE_URL} />
        <meta
          name="description"
          content="On-premise QNAP NAS and Microsoft 365 SharePoint and OneDrive setup in Westchester and Fairfield County. RAID storage, off-site NAS backups, and cloud file organization by Solace Integrations."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta
          property="og:title"
          content="Storage and Backup in Westchester &amp; Fairfield County | Solace Integrations"
        />
        <meta
          property="og:description"
          content="QNAP NAS installation and Microsoft 365 SharePoint and OneDrive setup for offices in Westchester and Fairfield County."
        />
        <meta property="og:image" content={`${SITE_ORIGIN}/assets/images/nas-deployment-placeholder.svg`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <Header isFixed />
      <section className="services">
        <div className="services-hero py-5">
          <div className="container py-md-5 py-2">
            <nav className="net-infra-breadcrumb" aria-label="Breadcrumb">
              <a href="/services">Services</a>
              <span className="net-infra-breadcrumb-sep" aria-hidden="true">/</span>
              <span>Storage &amp; Backup</span>
            </nav>
            <div className="text-center">
              <p className="unifi-page-hero-label">Storage &amp; Backup</p>
              <h1 className="services-title text-center mb-lg-4 mb-3 px-lg-5">
                Storage and Backup in Westchester &amp; Fairfield County
              </h1>
              <p className="main_p4 mt-4 mb-0 pt-2 text-center mx-auto text-white">
                On-premise QNAP NAS for offices that want local file storage, or Microsoft 365
                SharePoint and OneDrive for teams that work in the cloud. RAID, backups, and setup
                your staff can actually use.
              </p>
              <div className="unifi-page-hero-actions">
                <a href="/contact" className="btn btn-style solace-btn-primary">
                  Plan Your Storage Project
                </a>
                <a href="/contact" className="btn btn-style unifi-page-hero-secondary">
                  Talk With Us
                </a>
              </div>
              <p className="unifi-page-updated">Product lineup reviewed {LINEUP_UPDATED}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="overview" className="unifi-page-section">
        <div className="container">
          <div className="unifi-page-overview-grid">
            <div className="unifi-page-overview-main">
              <h6 className="solace-title-subhny mb-2">Overview</h6>
              <h2 className="title-w3l text-left">
                Local NAS or Microsoft 365, set up properly
              </h2>
              <p className="unifi-page-body">
                Solace Integrations helps businesses in Westchester and Fairfield County with two
                storage paths: rack mount QNAP NAS systems for on-premise file storage, and Microsoft
                365 SharePoint and OneDrive for cloud-based files. We do not support Google Workspace
                or other cloud platforms for file storage.
              </p>
              <p className="unifi-page-body">
                On-premise projects get RAID, shared folders, and Hybrid Backup Sync copying files
                off-site. Cloud projects get SharePoint sites, OneDrive sync, and permissions mapped
                before your team starts working. NAS handoffs include a sample backup restore.
                Microsoft 365 handoffs include documentation on built-in file recovery.
              </p>
              <div className="unifi-page-benefits">
                {KEY_BENEFITS.map(({ title, text }) => (
                  <article key={title} className="unifi-page-benefit-card">
                    <h3 className="unifi-page-benefit-title">{title}</h3>
                    <p className="unifi-page-benefit-text">{text}</p>
                  </article>
                ))}
              </div>
            </div>
            <aside className="unifi-page-snapshot">
              <h3 className="unifi-page-snapshot-title">At a Glance</h3>
              <dl className="unifi-page-snapshot-list">
                <div>
                  <dt>On-premise</dt>
                  <dd>QNAP NAS with RAID</dd>
                </div>
                <div>
                  <dt>Cloud storage</dt>
                  <dd>Microsoft 365 SharePoint &amp; OneDrive</dd>
                </div>
                <div>
                  <dt>Coverage area</dt>
                  <dd>Westchester &amp; Fairfield County</dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>{LINEUP_UPDATED}</dd>
                </div>
              </dl>
              <h4 className="unifi-page-snapshot-related">Related services</h4>
              <ul className="unifi-page-snapshot-links">
                <li>
                  <a href="/services">Storage &amp; Backup (All Services)</a>
                </li>
                <li>
                  <a href="/services/network-infrastructure">Network Infrastructure</a>
                </li>
                <li>
                  <a href="/products/unifi-networking">UniFi Networking</a>
                </li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      <section id="deliverables" className="net-infra-deliverables">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Design Deliverables</h6>
            <h2 className="title-w3l">What you get along the way</h2>
            <p className="net-infra-section-intro mx-auto">
              Whether you are sizing a NAS or planning SharePoint sites, we document the layout
              before anything is purchased or migrated.
            </p>
          </div>
          {DELIVERABLES.map((deliverable) => (
            <DeliverableSection key={deliverable.title} {...deliverable} />
          ))}
        </div>
      </section>

      <section id="on-premise" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">On-Premise</h6>
            <h2 className="title-w3l">QNAP NAS for local file storage</h2>
            <p className="net-infra-section-intro mx-auto">
              For offices that want files stored in the building, we install rack mount QNAP units
              with RAID and off-site backup through Hybrid Backup Sync.
            </p>
          </div>
          <div className="unifi-page-product-grid unifi-page-product-grid-wide">
            {ON_PREMISE_PRODUCTS.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
          <ul className="unifi-page-why-list mt-5">
            {WHY_QNAP.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="cloud-storage" className="unifi-page-section">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Cloud Storage</h6>
            <h2 className="title-w3l">Microsoft 365 SharePoint and OneDrive</h2>
            <p className="net-infra-section-intro mx-auto">
              For offices that keep files in the cloud, we set up SharePoint for shared team storage
              and OneDrive for individual staff. This is the only cloud file platform we support.
            </p>
          </div>
          <div className="unifi-page-product-grid">
            {CLOUD_PRODUCTS.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
          <ul className="unifi-page-why-list mt-5">
            {WHY_M365.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="deployment" className="net-infra-install">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Deployment</h6>
            <h2 className="title-w3l">How we set it up</h2>
            <p className="net-infra-section-intro mx-auto">
              On-premise and cloud projects follow different paths. NAS projects end with backup
              jobs configured and a sample restore. Cloud projects end with sites configured and
              recovery steps documented for your team.
            </p>
          </div>
          <DeliverableSection {...PROFESSIONAL_DEPLOYMENT} />
          <DeliverableSection {...CLOUD_DEPLOYMENT} />
        </div>
      </section>

      <section id="coverage" className="unifi-page-section">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Service Areas</h6>
            <h2 className="title-w3l">Westchester &amp; Fairfield County coverage</h2>
            <p className="net-infra-section-intro mx-auto">
              We support storage projects for professional offices and small businesses across
              Westchester and Fairfield County, including White Plains, Scarsdale, Darien,
              Greenwich, and Stamford.
            </p>
          </div>
          <ul className="unifi-page-areas">
            {SERVICE_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="faqs" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">FAQs</h6>
            <h2 className="title-w3l">Questions about storage and backup</h2>
          </div>
          <div className="unifi-page-faq-grid">
            {FAQS.map((faq) => (
              <FaqItem key={faq.question} {...faq} />
            ))}
          </div>
        </div>
      </section>

      <section className="net-infra-cta">
        <div className="container text-center">
          <h4 className="net-infra-cta-title">Ready to scope a storage and backup project?</h4>
          <p className="net-infra-cta-text">
            Tell us where your files live today and how your team works. We will outline an on-premise
            NAS plan, a Microsoft 365 setup, or both.
          </p>
          <a href="/contact" className="btn btn-style solace-btn-primary">
            Start a Conversation
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
